import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import api from "../../lib/api";
import adminApi from "../../lib/adminApi";
import { ADMIN } from "../../lib/adminI18n";
import { CATEGORIES } from "../../lib/catalog";
import { Loader2, Plus, Trash2 } from "lucide-react";

export default function StoresTab() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const [data, setData] = useState({ custom: [], hidden: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", category: "restaurang", color: "#0D9488" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await api.getStores()); }
    catch { toast.error(a.error); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await adminApi.createStore({ name: form.name.trim(), category: form.category, color: form.color });
      toast.success(a.done);
      setForm({ name: "", category: "restaurang", color: "#0D9488" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
    finally { setSaving(false); }
  };

  const remove = async (store) => {
    if (!window.confirm(a.confirmDelete)) return;
    try {
      await adminApi.deleteStore({ store_id: store.store_id, is_custom: true });
      toast.success(a.done);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
  };

  const custom = (data.custom || []);

  return (
    <div data-testid="admin-stores" className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-5 h-fit">
        <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Plus className="h-4 w-4" /> {a.createStore}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{a.storeName}</label>
            <input data-testid="admin-store-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{a.category}</label>
            <select data-testid="admin-store-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c[lang]} ({c.id})</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground block">{a.color}</label>
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-11 w-16 rounded-lg border border-border bg-background" />
            </div>
            <button data-testid="admin-store-create" disabled={saving} onClick={create} className="flex-1 h-11 mt-5 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : a.add}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-head font-bold mb-4">{a.yourStores}</h3>
        {loading ? (
          <div className="grid place-items-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : custom.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">{a.noStores}</div>
        ) : (
          <div className="space-y-2">
            {custom.map((s) => (
              <div key={s.store_id} data-testid={`admin-store-${s.store_id}`} className="flex items-center gap-3 rounded-xl border border-border p-3">
                <span className="h-8 w-8 rounded-lg shrink-0" style={{ background: s.color || "#0D9488" }} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.category} · {s.store_id}</div>
                </div>
                <button data-testid={`admin-store-delete-${s.store_id}`} onClick={() => remove(s)} className="h-9 w-9 rounded-lg bg-red-500/10 text-red-600 grid place-items-center hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
